import type { RegisteredRouter } from '@tanstack/react-router';

import type { SessionStoreState } from '@/hooks/use-session-store/session-store-schema';

import { EXIT_MILESTONE_IDS, MILESTONES } from './milestones';
import { FALLBACK_WIZARD_VARIANT_ID, WIZARD_VARIANTS } from './wizard.config';

export type RoutePath =
  RegisteredRouter['routesByPath'][keyof RegisteredRouter['routesByPath']]['fullPath'];

export type WizardVariantId = keyof typeof WIZARD_VARIANTS;

type MilestoneResolver = (state: SessionStoreState) => boolean;

export interface Milestone {
  id: MilestoneId;
  label: string;
  description: string;
  startPath: RoutePath;
  routes: RoutePath[];
  isComplete: MilestoneResolver;
}

export type MilestoneId = (typeof MILESTONES)[keyof typeof MILESTONES];

export interface ExitMilestone {
  id: MilestoneId;
  label: string;
  description: string;
  route: RoutePath;
}

export interface WizardVariantDefinition<TId extends string = string> {
  id: TId;
  name: string;
  startPath: RoutePath;
  milestones: Milestone[];
  exitMilestones: ExitMilestone[];
}

export type WizardVariant = WizardVariantDefinition<WizardVariantId>;

export interface WizardProgress {
  variant: WizardVariant;
  currentMilestone: Milestone | null;
  currentMilestoneIndex: number;
  exitMilestone: ExitMilestone | null;
  resumePath: RoutePath;
  completedMilestoneIds: MilestoneId[];
}

// =============================================================================
// Milestone Helpers
// =============================================================================

export function isExitMilestone(id: MilestoneId): boolean {
  return EXIT_MILESTONE_IDS.has(id);
}

export function hasExited(state: SessionStoreState): boolean {
  return state.exitMilestone != null && isExitMilestone(state.exitMilestone);
}

export function getCompletedMilestoneIds(
  state: SessionStoreState,
  milestones: readonly Milestone[]
): MilestoneId[] {
  return milestones.filter((m) => m.isComplete(state)).map((m) => m.id);
}

export function getFirstIncompleteMilestone(
  state: SessionStoreState,
  milestones: readonly Milestone[]
): Milestone | null {
  return milestones.find((m) => !m.isComplete(state)) ?? null;
}

export function getRemainingMilestones(
  state: SessionStoreState,
  milestones: readonly Milestone[]
): Milestone[] {
  const firstIncomplete = getFirstIncompleteMilestone(state, milestones);
  if (!firstIncomplete) return [];

  const index = milestones.indexOf(firstIncomplete);
  return milestones.slice(index) as Milestone[];
}

export function getResumePath(state: SessionStoreState, variant: WizardVariant): RoutePath {
  if (hasExited(state)) {
    const exit = variant.exitMilestones.find((t) => t.id === state.exitMilestone);
    return exit?.route ?? variant.startPath;
  }

  const firstIncomplete = getFirstIncompleteMilestone(state, variant.milestones);
  return firstIncomplete?.startPath ?? variant.startPath;
}

// =============================================================================
// Variant Helpers
// =============================================================================

export function getWizardVariant(variantId: WizardVariantId): WizardVariant {
  return WIZARD_VARIANTS[variantId] ?? WIZARD_VARIANTS[FALLBACK_WIZARD_VARIANT_ID]!;
}

export function findMilestoneByRoute(
  variant: WizardVariant,
  path: RoutePath
): Milestone | undefined {
  return variant.milestones.find((m) => m.routes.includes(path));
}

export function findExitMilestoneByRoute(
  variant: WizardVariant,
  path: RoutePath
): ExitMilestone | undefined {
  return variant.exitMilestones.find((t) => t.route === path);
}

export function isRouteInVariant(variant: WizardVariant, path: RoutePath): boolean {
  return Boolean(findMilestoneByRoute(variant, path) || findExitMilestoneByRoute(variant, path));
}

export function isRouteInAnyVariant(path: RoutePath): boolean {
  return Object.values(WIZARD_VARIANTS).some((variant) => isRouteInVariant(variant, path));
}

/**
 * Returns true if the user is trying to access a route beyond their current
 * progress in the wizard (i.e., skipping ahead to a future milestone).
 */
export function isSkippingAhead(
  path: RoutePath,
  sessionState: SessionStoreState,
  variantId?: WizardVariantId
): boolean {
  const variant = getWizardVariant(
    variantId ?? sessionState.wizardVariantId ?? FALLBACK_WIZARD_VARIANT_ID
  );
  const targetMilestone = findMilestoneByRoute(variant, path);

  // Routes not in any milestone are always accessible (e.g., /session/welcome-back, terminal pages)
  if (!targetMilestone) return false;

  const targetIndex = variant.milestones.indexOf(targetMilestone);
  const firstIncomplete = getFirstIncompleteMilestone(sessionState, variant.milestones);

  // All milestones complete - allow access to any
  if (!firstIncomplete) return false;

  const firstIncompleteIndex = variant.milestones.indexOf(firstIncomplete);

  // Skipping ahead if trying to access a milestone beyond the first incomplete
  return targetIndex > firstIncompleteIndex;
}
